import React, {useEffect, useRef, useState} from 'react';
import {TextField, Button, Box, Typography, Grid, CircularProgress, Breadcrumbs, Checkbox, FormControlLabel} from '@mui/material';
import {Link, useNavigate, useParams} from 'react-router-dom';
import api from '../../api';
import Loading from "../../components/Loading";
import {NumericFormat} from "react-number-format";
import DidNotLoad from "../../components/DidNotLoad"; // Adapte a importação conforme seu arquivo api.js

function EditPrinterPage() {
    const [printer, setPrinter] = useState({
        name: '',
        brand: '',
        model: '',
        consumption: '',
        price: '',
        active: true,
    });
    const { id } = useParams();  // Pega o id da impressora a ser editada
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        name: null,
        brand: null,
        model: null,
        price: null,
        consumption: null,
        has_errors: false
    });
    const [hasResponse, setHasResponse] = useState(false);

    useEffect(() => {
        api.get(`printers/${id}`)
            .then(response => {
                const data = response.data.data || {};
                setPrinter({
                    name: data.name || '',
                    brand: data.brand || '',
                    model: data.model || '',
                    consumption: data.power_consumption_watts ?? data.consumption ?? '',
                    price: data.acquisition_value ?? data.price ?? 0,
                    active: data.active !== false,
                });
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    const handleSubmit = (e) => {
        e.preventDefault();

        let hasError = false
        if (printer.name === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                name: 'O nome da impressora é obrigatório para distinguir uma da outra.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (printer.brand === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                brand: 'A marca do material é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (printer.model === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                model: 'O modelo da impressora é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (hasError) {
            return;
        }

        if (isSubmitting) return; // Evita duplo clique
        setIsSubmitting(true); // Ativa o estado de loading

        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

        const updatedPrinter = {
            name: printer.name,
            brand: printer.brand,
            model: printer.model,
            power_consumption_watts: parseInt(printer.consumption, 10),
            acquisition_value: parseInt(printer.price, 10),
            // Envia defaults para não falhar caso o backend exija.
            acquisition_date: today,
            hourly_printing_cost: 0,
            active: printer.active,
        };

        // Envia a requisição para atualizar a impressora
        api.put(`printers/${id}`, updatedPrinter)
            .then(response => {
                setHasResponse(true);
                console.log('Impressora atualizada com sucesso!', response.data);
                navigate('/admin/printers'); // Redireciona após o sucesso
            })
            .catch(error => {
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                    setHasResponse(true);
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false); // Finaliza o carregamento
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    if (isLoading) {
        return <Loading/>;
    }

    if (didNotLoad) {
        return <DidNotLoad/>
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/printers')} to={'/admin/printers'}>
                    Impressoras
                </Link>
                <Typography color="text.primary">Editando impressora</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Editar Impressora</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome"
                            fullWidth
                            value={printer.name}
                            onChange={(e) => setPrinter({ ...printer, name: e.target.value })}
                        />
                        {resultErrors.name && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.name}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Marca"
                            fullWidth
                            value={printer.brand}
                            onChange={(e) => setPrinter({ ...printer, brand: e.target.value })}
                        />
                        {resultErrors.brand && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.brand}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Modelo"
                            fullWidth
                            value={printer.model}
                            onChange={(e) => setPrinter({ ...printer, model: e.target.value })}
                        />
                        {resultErrors.model && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.model}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Consumo máximo (W)"
                            type="number"
                            fullWidth
                            value={printer.consumption}
                            onChange={(e) => setPrinter({ ...printer, consumption: e.target.value })}
                            inputMode="numeric"
                        />
                        {resultErrors.consumption && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.consumption}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Valor pago"
                            fullWidth
                            customInput={TextField}
                            value={(printer.price || 0) / 100}
                            onValueChange={(values) => {
                                const priceInCents = Math.round((values.floatValue || 0) * 100);
                                setPrinter((prev) => ({ ...prev, price: priceInCents }));
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                        />
                        {resultErrors.price && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.price}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={printer.active}
                                    onChange={(e) => setPrinter({ ...printer, active: e.target.checked })}
                                    color="primary"
                                />
                            }
                            label="Impressora ativa (inativas não aparecem nos cards de produção)"
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                   <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting} // Desativa o botão durante o envio
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Atualizando...' : 'Atualizar Impressora'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export default EditPrinterPage;
